import { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { COLORS, SHADOWS, INPUT_TOKENS, DETAIL_TOKENS } from '../../../styles/UISystem';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { searchPlaces } from '../../../services/location/locationService';

const DEBOUNCE_MS = 300;

/**
 * CityAutocomplete
 *
 * Place-search input for the Hometown ("Current City") field. Debounced
 * forward search via locationService.searchPlaces returns clean, geocodable
 * city/neighborhood strings. Selecting a suggestion stores its clean label
 * AND where it is (location is a primitive: the pick's own point, state and
 * country); free text is still allowed (kept in sync as the user types) so
 * nothing is lost if they don't pick — typed text carries no point, never a
 * guessed one.
 *
 * Renders the same field container as the other ProfileForm fields; the
 * suggestion dropdown reuses the app's spring-popover motion pattern
 * (see ContactTypeDropdown).
 *
 * @param {string} value - Current stored place string
 * @param {function} onChange - (label, place) — place is {label, lat, lng,
 *   stateCode, countryCode} on a pick (the label it was picked for travels
 *   with it, so a retyped hometown never keeps an old point), null while typing
 * @param {string} [placeholder]
 * @param {boolean} [bare] - the input alone (no pill container), styled by
 *   the surrounding field — the new Profile page's `.fact input`
 * @param {Object} [inputProps] - extra input props (the new page's
 *   focus/blur/keys/ref handlers)
 * @param {function} [onListShown] - called once each time the list appears
 *   (not per keystroke) — the new page lifts LIVE IN so the list has room
 * @param {boolean} [capped] - about four places, more scroll inside the list
 */
const CityAutocomplete = ({
  value,
  onChange,
  placeholder = 'City or neighborhood',
  bare = false,
  inputProps = {},
  onListShown,
  capped = false,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);

  // The list appearing (closed → showing), once — never per keystroke.
  const shown = open && suggestions.length > 0;
  const shownRef = useRef(onListShown);
  shownRef.current = onListShown;
  useEffect(() => {
    if (shown) shownRef.current?.();
  }, [shown]);

  // Debounced forward search — skips queries under 2 chars
  const runSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const places = await searchPlaces(q);
      setSuggestions(places);
    }, DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurRef.current) clearTimeout(blurRef.current);
    },
    []
  );

  const handleType = (e) => {
    const text = e.target.value;
    onChange(text, null);
    setOpen(true);
    runSearch(text);
  };

  const handleSelect = (place) => {
    const { label, lat, lng, stateCode, countryCode } = place;
    onChange(
      label,
      lat != null && lng != null ? { label, lat, lng, stateCode, countryCode } : null
    );
    setSuggestions([]);
    setOpen(false);
  };

  const { onFocus, onBlur, ...restInput } = inputProps;
  const input = (
    <input
      type="text"
      {...restInput}
      value={value || ''}
      onChange={handleType}
      onFocus={(e) => {
        if (suggestions.length) setOpen(true);
        onFocus?.(e);
      }}
      // Delay close so a suggestion tap (which blurs the input first) still registers
      onBlur={(e) => {
        blurRef.current = setTimeout(() => setOpen(false), 150);
        onBlur?.(e);
      }}
      placeholder={placeholder}
      autoCapitalize="words"
      autoCorrect="off"
      autoComplete="off"
      style={
        bare
          ? undefined
          : {
              flex: 1,
              border: 'none',
              fontSize: '17px',
              color: COLORS.DARK_GRAY,
              backgroundColor: 'transparent',
              outline: 'none',
            }
      }
    />
  );

  return (
    <div
      style={
        bare
          ? { position: 'relative' }
          : {
              position: 'relative',
              // The WIDE column, shared with the About-me fields — the contact-row
              // width could not hold a real location (user, 2026-08-20).
              width: DETAIL_TOKENS.ABOUT_ROW_WIDTH,
            }
      }
    >
      {bare ? (
        input
      ) : (
        /* Field container — identical to a compact ContactField row: same pill
           radius, same hairline border, same 52px height, same padding. */
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            minHeight: '52px',
            padding: '8px 16px',
            borderRadius: INPUT_TOKENS.CONTACT_RADIUS,
            border: INPUT_TOKENS.CONTACT_BORDER,
            backgroundColor: INPUT_TOKENS.CONTACT_BG_EDITABLE,
            // Same shape and width as a contact row, but slightly lifted (user,
            // 2026-08-20). TIER1, not the old TIER2 that came with no border and
            // a 12px radius — a hint of elevation, not a second treatment.
            boxShadow: SHADOWS.TIER1,
            boxSizing: 'border-box',
          }}
        >
          <MapPin size={20} weight="regular" color={COLORS.MEDIUM_GRAY} style={{ flexShrink: 0 }} />
          {input}
        </div>
      )}

      {/* Suggestion dropdown */}
      <AnimatePresence>
        {open && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -6 }}
            transition={{ type: 'spring', stiffness: 400, damping: 26, mass: 0.8 }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '0.5px solid rgba(0, 0, 0, 0.08)',
              boxShadow: SHADOWS.TIER3_UP,
              zIndex: 20,
              overflow: 'hidden',
              ...(capped && { overflowY: 'auto', maxHeight: '184px' }),
              transformOrigin: 'top center',
            }}
          >
            {suggestions.map((place, i) => (
              <button
                key={`${place.label}-${i}`}
                type="button"
                // Fire before blur so focus stays put and the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(place)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  borderTop: i > 0 ? '0.5px solid #F0F0F0' : 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.05)',
                }}
              >
                <MapPin
                  size={18}
                  weight="regular"
                  color={COLORS.MEDIUM_GRAY}
                  style={{ flexShrink: 0 }}
                />
                <span style={{ fontSize: '15px', color: COLORS.DARK_GRAY }}>{place.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CityAutocomplete;
